// src/hooks/useRealTimeHealthData.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './useAuth';
import iomtApiService from '../services/iomtApiService';
import { apiConfig } from '../config/apiConfig';

// Device priority configuration for selecting best data source
const DEVICE_PRIORITIES = {
  heartRate: ['apple-watch', 'garmin', 'fitbit', 'oura-ring', 'withings-sleep', 'iphone-healthkit'],
  bloodOxygen: ['apple-watch', 'garmin', 'fitbit', 'withings-sleep'],
  bloodPressure: ['omron-bp', 'withings-bpm', 'apple-watch'],
  temperature: ['oura-ring', 'tempdrop', 'withings-thermo', 'apple-watch'],
  steps: ['apple-watch', 'garmin', 'fitbit', 'iphone-healthkit', 'android-health'],
  bloodGlucose: ['dexcom-g6', 'freestyle-libre', 'one-touch'],
  sleep: ['oura-ring', 'withings-sleep', 'eight-sleep', 'apple-watch'],
  urineAnalysis: ['withings-uscan'],
  weight: ['withings-body', 'garmin-index', 'fitbit-aria']
};

// Healthcare professional ranges and thresholds
const HEALTH_RANGES = {
  heartRate: { 
    low: 60, 
    high: 100, 
    critical: { low: 50, high: 110 },
    unit: 'bpm',
    display: 'Heart Rate'
  },
  bloodOxygen: { 
    low: 95, 
    high: 100, 
    critical: { low: 90, high: 100 },
    unit: '%',
    display: 'SpO₂'
  },
  bloodPressure: {
    systolic: { low: 90, high: 120, critical: { low: 80, high: 130 } },
    diastolic: { low: 60, high: 80, critical: { low: 50, high: 90 } },
    unit: 'mmHg',
    display: 'Blood Pressure'
  },
  temperature: {
    low: 97.0,
    high: 99.0,
    critical: { low: 95.0, high: 103.0 },
    unit: '°F',
    display: 'Body Temp'
  },
  bloodGlucose: {
    fasting: { low: 70, high: 100, critical: { low: 54, high: 140 } },
    postMeal: { low: 80, high: 140, critical: { low: 70, high: 180 } },
    unit: 'mg/dL',
    display: 'Glucose'
  },
  respiratoryRate: {
    low: 12,
    high: 20,
    critical: { low: 8, high: 30 },
    unit: 'brpm',
    display: 'Resp Rate'
  }
};

const useRealTimeHealthData = () => {
  const { user, userInfo } = useAuth();
  const isFetchingRef = useRef(false);
  const intervalRef = useRef(null);
  
  // State for profile data from Azure Data Lake
  const [profileDataFromLake, setProfileDataFromLake] = useState(null);
  const [activePlansFromLake, setActivePlansFromLake] = useState([]);
  
  // Empty data structure to prevent null errors
  const EMPTY_HEALTH_DATA = useMemo(() => ({
    vitals: {
      heartRate: null,
      bloodOxygen: null,
      bloodPressure: null,
      temperature: null,
      respiratoryRate: null,
      bloodGlucose: null
    },
    activity: {
      steps: { value: 0, source: null, timestamp: null },
      calories: { value: 0, source: null, timestamp: null },
      distance: { value: 0, unit: 'km' },
      activeMinutes: 0
    },
    wellness: {
      sleep: null,
      hydration: { glasses: 0, ml: 0, target: 8, percentage: 0 },
      stress: null,
      nutrition: null
    },
    score: {
      overall: 0,
      status: 'No Data',
      components: {
        activity: 0,
        vitals: 0,
        sleep: 0,
        nutrition: 0,
        mental: 0
      },
      dataCompleteness: 0
    },
    realTimeMetrics: {
      heartRate: null,
      steps: 0,
      calories: 0,
      water: 0,
      sleep: null,
      glucose: null
    },
    devices: [],
    lastUpdated: new Date().toISOString(),
    dataQuality: {
      percentage: 0,
      level: 'No Data',
      availableMetrics: 0,
      totalMetrics: 8
    },
    alerts: [],
    recommendations: [],
    medications: [], // Add medications to the structure
    activePlans: [] // Add active plans to the structure
  }), []);

  const [healthData, setHealthData] = useState(EMPTY_HEALTH_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [dataAvailability, setDataAvailability] = useState({});

  // Fetch profile data from Azure Data Lake using hybrid approach
  const fetchProfileDataFromLake = useCallback(async () => {
    const userId = user?.localAccountId || userInfo?.localAccountId;
    // 🌍 DYNAMIC: Regional routing support - detects country for all 195 countries
    const country = userInfo?.country || 
                   userInfo?.personalDetails?.country ||
                   'United States'; // Default fallback
    
    console.log('🚀 fetchProfileDataFromLake started');
    console.log('📌 User ID:', userId);
    console.log('📌 Country:', country);
    
    if (!userId) {
      console.log('❌ No user ID available for fetching profile data');
      return null;
    }

    try {
      console.log('📁 Fetching profile data from Azure Data Lake...');
      
      // Check cache first (for performance with millions of users)
      const cacheKey = `medicureon_profile_${userId}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheExpiry = 1 * 60 * 1000; // 1 minute cache for faster testing
      
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        if (Date.now() - parsed.timestamp < cacheExpiry) {
          console.log('📦 Using cached profile data');
          setProfileDataFromLake(parsed.medicationsData);
          setActivePlansFromLake(parsed.activePlansData);
          return { medicationsData: parsed.medicationsData, activePlansData: parsed.activePlansData };
        }
      }
      
      // Construct URLs
      const profileUrl = `${apiConfig.backendUrl}/api/profiles/${userId}?code=${apiConfig.functionKeys.profileGet}`;
const plansUrl = `${apiConfig.backendUrl}/api/profiles/${userId}/active-plans?code=${apiConfig.functionKeys.getActivePlans || apiConfig.functionKeys.profileGet}`;
      
      console.log('📡 Profile URL:', profileUrl);
      console.log('📡 Plans URL:', plansUrl);
      
      // Fetch fresh data - parallel requests for better performance
      const [profilePromise, plansPromise] = await Promise.allSettled([
        // Get profile data (medications, lifestyle, etc.)
        fetch(profileUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }),
        // Get active plans directly (lightweight endpoint)
        fetch(plansUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        })
      ]);
      
      console.log('🔍 Profile Promise Result:', profilePromise);
      console.log('🔍 Plans Promise Result:', plansPromise);
      
      let medicationsData = null;
      let activePlansData = [];
      
      // Process profile response
      if (profilePromise.status === 'fulfilled' && profilePromise.value.ok) {
        const result = await profilePromise.value.json();
        console.log('🔍 Full profile response:', result);
        console.log('🔍 Profile data structure:', result.data);
        console.log('🔍 All data keys:', result.data ? Object.keys(result.data) : 'No data');
        console.log('🔍 Lifestyle data:', result.data?.lifestyle);
        console.log('🔍 Medications-lifestyle data:', result.data?.['medications-lifestyle']);
        console.log('🔍 Active plans in profile:', result.data?.activePlans);
        console.log('🔍 Active-plans in profile:', result.data?.['active-plans']);
        
        if (result.success && result.data) {
          console.log('✅ Fetched complete profile from Data Lake');
          // Try different possible locations for medications data
          medicationsData = result.data.lifestyle || 
                          result.data['medications-lifestyle'] || 
                          result.data.medicationsLifestyle || 
                          null;
          
          console.log('💊 Medications data found:', medicationsData);
          
          // Also check for active plans in the profile response
          if (result.data.activePlans || result.data['active-plans']) {
            activePlansData = result.data.activePlans || result.data['active-plans'];
            console.log('📋 Found active plans in profile response:', activePlansData);
          }
        }
      } else {
        console.log('❌ Profile fetch failed or not ok:', profilePromise);
        // Try to use existing state data if fetch failed
        medicationsData = profileDataFromLake;
        activePlansData = activePlansFromLake;
      }
      
      // Process active plans response
      if (plansPromise.status === 'fulfilled' && plansPromise.value.ok) {
        const plansResult = await plansPromise.value.json();
        console.log('🔍 Plans response:', plansResult);
        
        if (plansResult.data) {
          // Handle different response formats
          if (Array.isArray(plansResult.data)) {
            activePlansData = plansResult.data;
          } else if (plansResult.success && Array.isArray(plansResult.data)) {
            activePlansData = plansResult.data;
          } else if (plansResult.activePlans) {
            activePlansData = plansResult.activePlans;
          }
          console.log('✅ Fetched active plans data:', activePlansData);
        }
      } else {
        console.log('⚠️ Plans fetch failed or returned empty');
      }
      
      // Update state
      console.log('📊 Setting profile data:', medicationsData);
      console.log('📊 Setting active plans:', activePlansData);
      
      setProfileDataFromLake(medicationsData);
      setActivePlansFromLake(activePlansData);
      
      // Cache the data for performance
      sessionStorage.setItem(cacheKey, JSON.stringify({
        medicationsData,
        activePlansData,
        timestamp: Date.now()
      }));
      
      return { medicationsData, activePlansData };
    } catch (error) {
      console.error('❌ Error fetching profile data from Data Lake:', error);
      return null;
    }
  }, [user, userInfo]);

  // Transform Azure Data to professional healthcare format
  const transformAzureData = useCallback((azureResponse, profileData = null, activePlans = null) => {
    // Use passed data or fall back to state
    const currentProfileData = profileData || profileDataFromLake;
    const currentActivePlans = activePlans || activePlansFromLake;
    
    console.log('🔄 transformAzureData started');
    console.log('🔄 Azure response:', azureResponse);
    console.log('🔄 Profile data:', currentProfileData);
    console.log('🔄 Active plans:', currentActivePlans);
    
    // Extract medications from Azure Data Lake profile data
    const extractMedicationsFromProfile = () => {
      let allMedications = [];
      
      console.log('🔍 profileData in extract:', currentProfileData);
      console.log('🔍 activePlans in extract:', currentActivePlans);
      
      if (currentProfileData) {
        console.log('📊 Extracting medications from Azure Data Lake');
        
        // Get medications from the Data Lake structure
        const currentMedications = currentProfileData.currentMedications || [];
        const supplements = currentProfileData.supplements?.vitaminsSupplements || [];
        const herbalRemedies = currentProfileData.supplements?.herbalRemedies || [];
        
        console.log('🔍 Current medications:', currentMedications);
        console.log('🔍 Supplements:', supplements);
        console.log('🔍 Herbal remedies:', herbalRemedies);
        
        // Format prescription medications
        if (Array.isArray(currentMedications)) {
          allMedications.push(...currentMedications.map(med => ({
            name: med.name || 'Unknown Medication',
            dosage: med.dosage || 'As prescribed',
            frequency: med.frequency || 'As directed',
            type: 'prescription',
            prescribingDoctor: med.prescribingDoctor || '',
            purpose: med.purpose || '',
            startDate: med.startDate || '',
            endDate: med.endDate || '',
            sideEffects: med.sideEffects || '',
            time: med.frequency === 'Once daily' ? '9:00 AM' : 
                  med.frequency === 'Twice daily' ? '8:00 AM, 8:00 PM' : 
                  med.frequency === 'Three times daily' ? '8:00 AM, 2:00 PM, 8:00 PM' : 'As needed',
            taken: false // This would be tracked separately in real app
          })));
        }
        
        // Format supplements (vitamins)
        if (Array.isArray(supplements)) {
          allMedications.push(...supplements
            .filter(supp => supp && supp.trim() !== '' && supp.trim() !== ',')
            .map(supp => ({
              name: supp.replace(/,$/, '').trim(), // Remove trailing commas
              dosage: 'As directed',
              frequency: 'Daily',
              type: 'supplement',
              time: '9:00 AM',
              taken: false
            })));
        }
        
        // Format herbal remedies
        if (Array.isArray(herbalRemedies)) {
          allMedications.push(...herbalRemedies
            .filter(herb => herb && herb.trim() !== '' && herb.trim() !== ',')
            .map(herb => ({
              name: herb.replace(/,$/, '').trim(), // Remove trailing commas
              dosage: 'As directed',
              frequency: 'As needed',
              type: 'herbal',
              time: 'As needed',
              taken: false
            })));
        }
        
        console.log(`✅ Found ${allMedications.length} medications from Azure Data Lake`);
      } else {
        console.log('⚠️ No profile data available for medications extraction');
      }
      
      return allMedications;
    };
    
    if (!azureResponse?.data?.length) {
      console.log('❌ No data in response, returning with profile data only');
      const medications = extractMedicationsFromProfile();
      return { ...EMPTY_HEALTH_DATA, medications, activePlans: currentActivePlans || [] };
    }

    // Log the first record completely
    console.log('📊 First record full structure:', JSON.stringify(azureResponse.data[0], null, 2));
    
    // Check the actual data structure
    const firstRecord = azureResponse.data[0];
    console.log('🔍 Checking data paths:');
    console.log('- healthMetrics:', firstRecord.healthMetrics);
    console.log('- activity:', firstRecord.healthMetrics?.activity);
    console.log('- steps:', firstRecord.healthMetrics?.activity?.steps);
    console.log('- steps value:', firstRecord.healthMetrics?.activity?.steps?.value);
    
    // Group files by date to get latest
    const filesByDate = {};
    
    azureResponse.data.forEach(record => {
      const fileName = record._metadata?.fileName || '';
      const dateMatch = fileName.match(/(\d{8})/);
      
      if (dateMatch) {
        const dateKey = dateMatch[1];
        if (!filesByDate[dateKey]) {
          filesByDate[dateKey] = [];
        }
        filesByDate[dateKey].push(record);
      }
    });
    
    console.log('📅 Files by date:', Object.keys(filesByDate));
    
    // Enhanced logging to show all Apple Watch files detected
    console.log('🍎 Apple Watch Files Analysis:');
    azureResponse.data.forEach((record, index) => {
      const fileName = record._metadata?.fileName || `file-${index}`;
      const stepsValue = record?.healthMetrics?.activity?.steps?.value;
      const heartRate = record?.healthMetrics?.vitalSigns?.heartRate?.value;
      const timestamp = record?.timestamp;
      
      console.log(`📱 File ${index + 1}: ${fileName}`);
      console.log(`   📊 Steps: ${stepsValue || 'N/A'}`);
      console.log(`   ❤️ Heart Rate: ${heartRate || 'N/A'}`);
      console.log(`   🕐 Timestamp: ${timestamp || 'N/A'}`);
      console.log(`   📦 File Size: ${JSON.stringify(record).length} chars`);
      console.log('   ---');
    });
    
    // Get the latest file
    const sortedDates = Object.keys(filesByDate).sort((a, b) => b.localeCompare(a));
    const latestDate = sortedDates[0];
    const latestFiles = filesByDate[latestDate] || [];
    
    // Sort by time to get latest file of the day - Enhanced pattern matching
    const latestFile = latestFiles.sort((a, b) => {
      const fileNameA = a._metadata?.fileName || '';
      const fileNameB = b._metadata?.fileName || '';
      
      // Multiple time format patterns:
      // Pattern 1: _HHMM- (e.g., _1616-)
      // Pattern 2: THHMMSS (e.g., T154251, T170000) 
      // Pattern 3: Extract from timestamp if available
      let timeA = '000000';
      let timeB = '000000';
      
      // Try different time extraction patterns
      const timeMatchA = fileNameA.match(/_(\d{4})-/) || 
                        fileNameA.match(/T(\d{6})/) || 
                        fileNameA.match(/T(\d{4})(\d{2})/);
      const timeMatchB = fileNameB.match(/_(\d{4})-/) || 
                        fileNameB.match(/T(\d{6})/) || 
                        fileNameB.match(/T(\d{4})(\d{2})/);
      
      if (timeMatchA) {
        timeA = timeMatchA[1] + (timeMatchA[2] || '00'); // Add seconds if missing
      }
      if (timeMatchB) {
        timeB = timeMatchB[1] + (timeMatchB[2] || '00'); // Add seconds if missing
      }
      
      // If no time found, use file creation timestamp
      if (timeA === '000000' && a.timestamp) {
        const date = new Date(a.timestamp);
        timeA = date.toTimeString().replace(/:/g, '').substring(0, 6);
      }
      if (timeB === '000000' && b.timestamp) {
        const date = new Date(b.timestamp);
        timeB = date.toTimeString().replace(/:/g, '').substring(0, 6);
      }
      
      console.log(`🕐 Time comparison: ${fileNameA} (${timeA}) vs ${fileNameB} (${timeB})`);
      return timeB.localeCompare(timeA); // Latest time first
    })[0] || azureResponse.data[0];
    
    console.log('📦 Using file:', latestFile?._metadata?.fileName);
    console.log('📊 Steps value in file:', latestFile?.healthMetrics?.activity?.steps?.value);
    
    // ENHANCED: Option to aggregate data from multiple files of the same day
    // This ensures we don't lose data from your other 4 Apple Watch files
    const shouldAggregateFiles = true; // Set to true to combine data from all files
    
    let finalHealthData = latestFile;
    
    if (shouldAggregateFiles && latestFiles.length > 1) {
      console.log(`🔄 Aggregating data from ${latestFiles.length} files from ${latestDate}`);
      
      // Combine data from all files, using the highest/best values
      finalHealthData = {
        ...latestFile,
        healthMetrics: {
          vitalSigns: {
            heartRate: getBestValue(latestFiles.map(f => f.healthMetrics?.vitalSigns?.heartRate)),
            bloodOxygen: getBestValue(latestFiles.map(f => f.healthMetrics?.vitalSigns?.bloodOxygen)),
            bodyTemperature: getBestValue(latestFiles.map(f => f.healthMetrics?.vitalSigns?.bodyTemperature))
          },
          activity: {
            steps: getBestValue(latestFiles.map(f => f.healthMetrics?.activity?.steps), 'max'),
            activeCalories: getBestValue(latestFiles.map(f => f.healthMetrics?.activity?.activeCalories), 'max'),
            activeMinutes: getBestValue(latestFiles.map(f => f.healthMetrics?.activity?.activeMinutes), 'max'),
            distance: getBestValue(latestFiles.map(f => f.healthMetrics?.activity?.distance), 'max'),
            standHours: getBestValue(latestFiles.map(f => f.healthMetrics?.activity?.standHours), 'max')
          },
          lifestyle: {
            // 🌙 FIX: Preserve sleep data from lifestyle section
            sleep: getBestValue(latestFiles.map(f => f.healthMetrics?.lifestyle?.sleep)),
            waterIntake: getBestValue(latestFiles.map(f => f.healthMetrics?.lifestyle?.waterIntake), 'max'),
            mindfulness: getBestValue(latestFiles.map(f => f.healthMetrics?.lifestyle?.mindfulness))
          },
          wellness: {
            stress: getBestValue(latestFiles.map(f => f.healthMetrics?.wellness?.stress)),
            hydration: getBestValue(latestFiles.map(f => f.healthMetrics?.wellness?.hydration), 'max'),
            mindfulness: getBestValue(latestFiles.map(f => f.healthMetrics?.wellness?.mindfulness))
          }
        }
      };
      
      console.log('✅ Aggregated health data created from multiple Apple Watch files');
      console.log('📊 Final steps value:', finalHealthData?.healthMetrics?.activity?.steps?.value);
    }
    
    // Helper function to get the best value from multiple files
    function getBestValue(values, strategy = 'latest') {
      const validValues = values.filter(v => v && v.value !== null && v.value !== undefined);
      if (validValues.length === 0) return null;
      
      if (strategy === 'max') {
        return validValues.reduce((best, current) => 
          current.value > best.value ? current : best
        );
      } else if (strategy === 'latest') {
        return validValues.reduce((best, current) => {
          const bestTime = new Date(best.startDate || best.timestamp || 0);
          const currentTime = new Date(current.startDate || current.timestamp || 0);
          return currentTime > bestTime ? current : best;
        });
      }
      return validValues[0];
    }
    
    // Extract metrics directly from final aggregated data
    const steps = finalHealthData?.healthMetrics?.activity?.steps?.value || 0;
    const calories = finalHealthData?.healthMetrics?.activity?.caloriesBurned?.value || 
                    finalHealthData?.healthMetrics?.activity?.activeCalories?.value || 0;
    const heartRate = finalHealthData?.healthMetrics?.vitalSigns?.heartRate?.value || null;
    const waterML = finalHealthData?.healthMetrics?.lifestyle?.waterIntake?.value || 
                   finalHealthData?.healthMetrics?.wellness?.hydration?.value || 0;
    const sleepHours = finalHealthData?.healthMetrics?.lifestyle?.sleep?.value || 
                      finalHealthData?.healthMetrics?.sleep?.sleep?.totalSleep || null;
    
    // 🌙 SLEEP DEBUG: Let's see why sleep is null
    console.log('🌙 SLEEP EXTRACTION DEBUG:');
    console.log('- finalHealthData?.healthMetrics?.lifestyle?.sleep:', finalHealthData?.healthMetrics?.lifestyle?.sleep);
    console.log('- finalHealthData?.healthMetrics?.lifestyle?.sleep?.value:', finalHealthData?.healthMetrics?.lifestyle?.sleep?.value);
    console.log('- finalHealthData?.healthMetrics?.sleep?.sleep?.totalSleep:', finalHealthData?.healthMetrics?.sleep?.sleep?.totalSleep);
    console.log('- sleepHours result:', sleepHours);
    
    console.log('🎯 Final extracted values:');
    console.log(`📊 Steps: ${steps}`);
    console.log(`🔥 Calories: ${calories}`);
    console.log(`❤️ Heart Rate: ${heartRate}`);
    console.log(`💧 Water: ${waterML} ml`);
    console.log(`😴 Sleep: ${sleepHours} hours`);
    
    // Get medications from profile
    const medications = extractMedicationsFromProfile();
    
    // Extract stress level from profile data
    const stressLevel = currentProfileData?.mentalWellness?.stressLevels || 'Unknown';
    
    // Create debug information for Apple Watch files
    const debugInfo = {
      appleWatchFiles: {
        total: latestFiles?.length || 0,
        aggregated: shouldAggregateFiles && latestFiles?.length > 1,
        current: latestFile?._metadata?.fileName || 'None',
        allFiles: latestFiles?.map(f => f._metadata?.fileName).join(', ') || 'None'
      }
    };
    
    console.log('🎯 Final medications array:', medications);
    console.log('🎯 Final active plans:', activePlansFromLake);
    console.log('🎯 Steps value:', steps);
    console.log('✅ Extracted values:', { 
      steps, 
      calories, 
      heartRate, 
      waterML, 
      sleepHours, 
      medicationsCount: medications.length, 
      activePlansCount: activePlansFromLake ? activePlansFromLake.length : 0 
    });
    
    // Calculate simple scores
    const activityScore = Math.min(100, Math.round((steps / 10000) * 100));
    const overallScore = activityScore || 0;
    
    // Build the response
    const result = {
      vitals: {
        heartRate: heartRate ? { value: heartRate, unit: 'bpm', status: 'normal' } : null,
        bloodOxygen: null,
        bloodPressure: null,
        temperature: null,
        respiratoryRate: null,
        bloodGlucose: null
      },
      activity: {
        steps: { value: steps, source: 'iphone-healthkit', timestamp: latestFile?._metadata?.fileDate },
        calories: { value: calories, source: 'iphone-healthkit', timestamp: latestFile?._metadata?.fileDate },
        distance: { value: ((steps * 0.762) / 1000).toFixed(1), unit: 'km' },
        activeMinutes: Math.round(steps / 100)
      },
      wellness: {
        sleep: sleepHours ? { duration: sleepHours, quality: null, source: 'iphone-healthkit' } : null,
        hydration: { glasses: Math.round(waterML / 250), ml: waterML, target: 8, percentage: Math.round((waterML / 2000) * 100) },
        stress: stressLevel, // Use actual stress level from profile
        nutrition: null
      },
      score: {
        overall: overallScore,
        status: overallScore >= 80 ? 'Good' : overallScore >= 60 ? 'Fair' : 'Needs Attention',
        components: {
          activity: activityScore,
          vitals: 0,
          sleep: 0,
          nutrition: 0,
          mental: 0
        },
        dataCompleteness: 25
      },
      realTimeMetrics: {
        heartRate: heartRate,
        steps: steps,
        calories: calories,
        water: Math.round(waterML / 250),
        sleep: sleepHours,
        stress: stressLevel, // Use actual stress level
        glucose: null
      },
      medications: medications, // Include medications from Azure Data Lake
      activePlans: currentActivePlans || [], // Include active plans from Azure Data Lake
      devices: [{
        name: 'iPhone Health',
        type: 'iphone-healthkit',
        connected: true,
        lastSync: latestFile?._metadata?.fileDate
      }],
      lastUpdated: new Date().toISOString(),
      dataQuality: {
        percentage: 25,
        level: 'Limited',
        availableMetrics: 2,
        totalMetrics: 8
      },
      alerts: [],
      recommendations: steps < 5000 ? [{
        category: 'activity',
        priority: 'high',
        message: 'Increase daily steps for better health',
        target: '10,000 steps/day'
      }] : [],
      debugInfo: debugInfo // Add Apple Watch files debug information
    };
    
    console.log('🎯 Final transformed result:', result);
    console.log('💊 Total medications in result:', result.medications.length);
    console.log('📋 Active plans in result:', result.activePlans.length);
    
    // Set data availability
    setDataAvailability({
      heartRate: !!heartRate,
      bloodOxygen: false,
      bloodPressure: false,
      temperature: false,
      steps: true,
      bloodGlucose: false,
      sleep: !!sleepHours,
      hydration: true
    });
    
    return result;
  }, [EMPTY_HEALTH_DATA]); // Remove state dependencies

  // Extract metrics from a device record (keeping existing code)
  const extractMetricsFromRecord = (record, deviceType) => {
    const metrics = {};
    const healthMetrics = record.healthMetrics || record.rawData?.metrics || {};
    
    console.log(`📊 Extracting metrics from ${deviceType}:`, healthMetrics);
    
    // Extract based on device capabilities
    switch (deviceType) {
      case 'iphone-healthkit':
      case 'apple-watch':
        // Steps - check for value even if it's 0
        if (healthMetrics.activity?.steps?.value !== undefined && healthMetrics.activity?.steps?.value !== null) {
          metrics.steps = {
            value: healthMetrics.activity.steps.value,
            timestamp: healthMetrics.activity.steps.timestamp,
            source: deviceType
          };
          console.log(`✅ Found steps: ${healthMetrics.activity.steps.value}`);
        }
        
        // Heart Rate
        if (healthMetrics.vitalSigns?.heartRate?.value > 0) {
          metrics.heartRate = {
            value: healthMetrics.vitalSigns.heartRate.value,
            timestamp: healthMetrics.vitalSigns.heartRate.timestamp,
            source: deviceType
          };
        }
        
        // Blood Oxygen
        if (healthMetrics.vitalSigns?.bloodOxygen?.value > 0) {
          metrics.bloodOxygen = {
            value: healthMetrics.vitalSigns.bloodOxygen.value,
            timestamp: healthMetrics.vitalSigns.bloodOxygen.timestamp,
            source: deviceType
          };
        }
        
        // Calories
        if (healthMetrics.activity?.caloriesBurned?.value !== undefined) {
          metrics.calories = {
            value: healthMetrics.activity.caloriesBurned.value || 0,
            timestamp: healthMetrics.activity.caloriesBurned.timestamp,
            source: deviceType
          };
        }
        
        // Sleep
        if (healthMetrics.lifestyle?.sleep?.value !== undefined) {
          metrics.sleep = {
            duration: healthMetrics.lifestyle.sleep.value || 0,
            timestamp: healthMetrics.lifestyle.sleep.timestamp,
            source: deviceType
          };
        }
        
        // Water
        if (healthMetrics.lifestyle?.waterIntake?.value !== undefined) {
          const waterML = healthMetrics.lifestyle.waterIntake.value || 0;
          metrics.hydration = {
            ml: waterML,
            glasses: Math.round(waterML / 250),
            timestamp: healthMetrics.lifestyle.waterIntake.timestamp,
            source: deviceType
          };
        }
        
        // Temperature - FIXED: Exclude 0 values that would show as 32°F
        if (healthMetrics.vitalSigns?.bodyTemperature?.value && 
            healthMetrics.vitalSigns.bodyTemperature.value !== 0 && 
            healthMetrics.vitalSigns.bodyTemperature.value > 0) {
          let tempValue = healthMetrics.vitalSigns.bodyTemperature.value;
          
          // Additional validation: Ensure realistic temperature range before conversion
          const isValidCelsius = tempValue >= 30 && tempValue <= 45; // 30-45°C (86-113°F)
          const isValidFahrenheit = tempValue >= 86 && tempValue <= 113; // Direct Fahrenheit
          
          if (isValidCelsius || isValidFahrenheit) {
            // Convert celsius to fahrenheit if needed
            if (healthMetrics.vitalSigns.bodyTemperature.unit === 'celsius') {
              tempValue = (tempValue * 9/5) + 32;
            }
            metrics.temperature = {
              value: tempValue,
              timestamp: healthMetrics.vitalSigns.bodyTemperature.timestamp,
              source: deviceType
            };
            console.log('🌡️ VALID TEMPERATURE PROCESSED:', tempValue, '°F from', healthMetrics.vitalSigns.bodyTemperature.value, healthMetrics.vitalSigns.bodyTemperature.unit);
          } else {
            console.log('🌡️ INVALID TEMPERATURE RANGE REJECTED:', tempValue, 'from source:', deviceType);
          }
        } else {
          console.log('🌡️ TEMPERATURE REJECTED (null/0/invalid):', healthMetrics.vitalSigns?.bodyTemperature?.value, 'from source:', deviceType);
        }
        
        // Blood Pressure
        if (healthMetrics.vitalSigns?.bloodPressure?.systolic > 0) {
          metrics.bloodPressure = {
            systolic: healthMetrics.vitalSigns.bloodPressure.systolic,
            diastolic: healthMetrics.vitalSigns.bloodPressure.diastolic,
            timestamp: healthMetrics.vitalSigns.bloodPressure.timestamp,
            source: deviceType
          };
        }
        
        // Respiratory Rate
        if (healthMetrics.vitalSigns?.respiratoryRate?.value > 0) {
          metrics.respiratoryRate = {
            value: healthMetrics.vitalSigns.respiratoryRate.value,
            timestamp: healthMetrics.vitalSigns.respiratoryRate.timestamp,
            source: deviceType
          };
        }
        break;
        
      case 'dexcom-g6':
      case 'dexcom':
        if (healthMetrics.bloodGlucose?.current?.value > 0) {
          metrics.bloodGlucose = {
            current: healthMetrics.bloodGlucose.current.value,
            trend: healthMetrics.bloodGlucose.trend,
            timestamp: healthMetrics.bloodGlucose.current.timestamp,
            source: deviceType
          };
        }
        break;
        
      case 'withings-uscan':
        if (healthMetrics.urineAnalysis) {
          metrics.urineAnalysis = {
            ph: healthMetrics.urineAnalysis.pH,
            ketones: healthMetrics.urineAnalysis.ketones,
            hydration: healthMetrics.urineAnalysis.hydration,
            timestamp: healthMetrics.urineAnalysis.timestamp,
            source: deviceType
          };
        }
        break;
        
      case 'withings-sleep':
        if (healthMetrics.sleepAnalysis?.duration > 0) {
          metrics.sleep = {
            duration: healthMetrics.sleepAnalysis.duration,
            score: healthMetrics.sleepAnalysis.sleepScore,
            stages: healthMetrics.sleepAnalysis.stages,
            timestamp: healthMetrics.sleepAnalysis.timestamp,
            source: deviceType
          };
        }
        break;
        
      case 'omron-bp':
      case 'withings-bpm':
        if (healthMetrics.vitalSigns?.bloodPressure?.systolic > 0) {
          metrics.bloodPressure = {
            systolic: healthMetrics.vitalSigns.bloodPressure.systolic,
            diastolic: healthMetrics.vitalSigns.bloodPressure.diastolic,
            timestamp: healthMetrics.vitalSigns.bloodPressure.timestamp,
            source: deviceType
          };
        }
        break;
        
      case 'withings-body':
      case 'garmin-index':
        if (healthMetrics.bodyComposition?.weight > 0) {
          metrics.weight = {
            value: healthMetrics.bodyComposition.weight,
            unit: healthMetrics.bodyComposition.unit || 'kg',
            timestamp: healthMetrics.bodyComposition.timestamp,
            source: deviceType
          };
        }
        break;
        
      default:
        // For unknown device types, try to extract common metrics
        console.log(`Unknown device type: ${deviceType}, attempting generic extraction`);
        
        // Try to extract any available metrics generically
        if (healthMetrics.activity?.steps?.value > 0) {
          metrics.steps = {
            value: healthMetrics.activity.steps.value,
            timestamp: healthMetrics.activity.steps.timestamp,
            source: deviceType
          };
        }
        if (healthMetrics.vitalSigns?.heartRate?.value > 0) {
          metrics.heartRate = {
            value: healthMetrics.vitalSigns.heartRate.value,
            timestamp: healthMetrics.vitalSigns.heartRate.timestamp,
            source: deviceType
          };
        }
        break;
    }
    
    return metrics;
  };

  // Aggregate metrics from multiple devices using priority
  const aggregateHealthMetrics = (allMetrics) => {
    const aggregated = {};
    
    // For each metric type, find the best source
    Object.entries(DEVICE_PRIORITIES).forEach(([metricType, devicePriority]) => {
      for (const device of devicePriority) {
        if (allMetrics[device]?.[metricType]) {
          aggregated[metricType] = allMetrics[device][metricType];
          break;
        }
      }
    });
    
    return aggregated;
  };

  // Format vital metric with status
  const formatVitalMetric = (metric, range) => {
    if (!metric?.value) return null;
    
    const value = metric.value;
    let status = 'normal';
    let risk = 'none';
    
    if (value < range.critical.low || value > range.critical.high) {
      status = 'critical';
      risk = 'high';
    } else if (value < range.low || value > range.high) {
      status = 'abnormal';
      risk = 'moderate';
    }
    
    return {
      value,
      unit: range.unit,
      status,
      risk,
      source: metric.source,
      timestamp: metric.timestamp,
      display: range.display
    };
  };

  // Format blood pressure specifically
  const formatBloodPressure = (bpData) => {
    if (!bpData?.systolic || !bpData?.diastolic) return null;
    
    const systolicStatus = bpData.systolic < 90 ? 'low' : bpData.systolic > 140 ? 'high' : 'normal';
    const diastolicStatus = bpData.diastolic < 60 ? 'low' : bpData.diastolic > 90 ? 'high' : 'normal';
    
    return {
      systolic: bpData.systolic,
      diastolic: bpData.diastolic,
      status: systolicStatus === 'high' || diastolicStatus === 'high' ? 'high' : 
              systolicStatus === 'low' || diastolicStatus === 'low' ? 'low' : 'normal',
      risk: (systolicStatus !== 'normal' || diastolicStatus !== 'normal') ? 'moderate' : 'none',
      display: `${bpData.systolic}/${bpData.diastolic}`,
      unit: 'mmHg',
      source: bpData.source,
      timestamp: bpData.timestamp
    };
  };

  // Format blood glucose metric
  const formatGlucoseMetric = (glucoseData) => {
    if (!glucoseData?.current) return null;
    let status = 'normal';
    let risk = 'none';
    const value = glucoseData.current;
    // Use HEALTH_RANGES for thresholds if available
    const fastingRange = HEALTH_RANGES.bloodGlucose.fasting;
    if (value < fastingRange.critical.low || value > fastingRange.critical.high) {
      status = 'critical';
      risk = 'high';
    } else if (value < fastingRange.low || value > fastingRange.high) {
      status = 'abnormal';
      risk = 'moderate';
    }
    return {
      value,
      unit: HEALTH_RANGES.bloodGlucose.unit,
      status,
      risk,
      trend: glucoseData.trend,
      source: glucoseData.source,
      timestamp: glucoseData.timestamp,
      display: HEALTH_RANGES.bloodGlucose.display
    };
  };

  
  // Calculate professional health scores
  const calculateHealthScores = (data) => {
    const scores = {
      activity: 0,
      vitals: 0,
      sleep: 0,
      nutrition: 0,
      mental: 0
    };
    
    // Activity score based on steps and active minutes
    if (data.steps?.value) {
      scores.activity = Math.min(100, Math.round((data.steps.value / 10000) * 100));
    }
    
    // Vitals score based on available vital signs
    let vitalCount = 0;
    let vitalScore = 0;
    
    if (data.heartRate?.value) {
      const hr = data.heartRate.value;
      if (hr >= 60 && hr <= 100) {
        vitalScore += 100;
      } else if (hr >= 50 && hr <= 110) {
        vitalScore += 70;
      } else {
        vitalScore += 40;
      }
      vitalCount++;
    }
    
    if (data.bloodOxygen?.value) {
      const spo2 = data.bloodOxygen.value;
      vitalScore += spo2 >= 95 ? 100 : spo2 >= 90 ? 70 : 40;
      vitalCount++;
    }
    
    scores.vitals = vitalCount > 0 ? Math.round(vitalScore / vitalCount) : 0;
    
    // Sleep score
    if (data.sleep?.duration) {
      const hours = data.sleep.duration;
      scores.sleep = hours >= 7 && hours <= 9 ? 100 : 
                     hours >= 6 && hours <= 10 ? 80 : 
                     hours >= 5 && hours <= 11 ? 60 : 40;
    }
    
    // Overall score
    const activeScores = Object.entries(scores).filter(([_, score]) => score > 0);
    const overall = activeScores.length > 0 
      ? Math.round(activeScores.reduce((sum, [_, score]) => sum + score, 0) / activeScores.length)
      : 0;
    
    return {
      overall,
      status: overall >= 90 ? 'Excellent' : 
              overall >= 80 ? 'Very Good' :
              overall >= 70 ? 'Good' :
              overall >= 60 ? 'Fair' : 'Needs Attention',
      components: scores,
      dataCompleteness: Math.round((activeScores.length / 5) * 100)
    };
  };

  // Generate health alerts based on data
  const generateHealthAlerts = (data) => {
    const alerts = [];
    
    if (data.heartRate?.value) {
      if (data.heartRate.value < 50) {
        alerts.push({
          type: 'warning',
          metric: 'heartRate',
          message: 'Heart rate is below normal resting range',
          severity: 'moderate'
        });
      } else if (data.heartRate.value > 100) {
        alerts.push({
          type: 'warning',
          metric: 'heartRate',
          message: 'Elevated heart rate detected',
          severity: 'moderate'
        });
      }
    }
    
    if (data.bloodGlucose?.current) {
      if (data.bloodGlucose.current < 70) {
        alerts.push({
          type: 'critical',
          metric: 'bloodGlucose',
          message: 'Low blood glucose level',
          severity: 'high'
        });
      } else if (data.bloodGlucose.current > 180) {
        alerts.push({
          type: 'warning',
          metric: 'bloodGlucose',
          message: 'High blood glucose level',
          severity: 'high'
        });
      }
    }
    
    return alerts;
  };

  // Helper functions
  const calculateDistance = (steps) => {
    if (!steps) return { value: 0, unit: 'km' };
    const km = (steps * 0.762) / 1000;
    return {
      value: km.toFixed(1),
      unit: 'km',
      miles: (km * 0.621371).toFixed(1)
    };
  };

  const calculateActiveMinutes = (steps, heartRate) => {
    if (!steps) return 0;
    // Basic calculation - can be enhanced with heart rate zones
    return Math.round(steps / 100);
  };

  const calculateStressLevel = (data) => {
    // Based on HRV, heart rate, and other factors
    if (!data.heartRate?.value) return null;
    
    const hr = data.heartRate.value;
    if (hr > 90) return 'High';
    if (hr > 75) return 'Moderate';
    return 'Low';
  };

  const formatSleepData = (sleepData) => {
    if (!sleepData) return null;
    
    return {
      duration: sleepData.duration || 0,
      quality: sleepData.score || null,
      stages: sleepData.stages || null,
      source: sleepData.source,
      timestamp: sleepData.timestamp
    };
  };

  const formatHydrationData = (hydrationData) => {
    if (!hydrationData) return { glasses: 0, ml: 0 };
    
    return {
      glasses: hydrationData.glasses || Math.round((hydrationData.ml || 0) / 250),
      ml: hydrationData.ml || (hydrationData.glasses || 0) * 250,
      target: 8,
      percentage: Math.round(((hydrationData.glasses || 0) / 8) * 100)
    };
  };

  const determineDataAvailability = (data) => {
    return {
      heartRate: !!data.heartRate?.value,
      bloodOxygen: !!data.bloodOxygen?.value,
      bloodPressure: !!data.bloodPressure?.systolic,
      temperature: !!data.temperature?.value,
      steps: !!data.steps?.value,
      bloodGlucose: !!data.bloodGlucose?.current,
      sleep: !!data.sleep?.duration,
      hydration: !!data.hydration?.glasses
    };
  };

  const calculateDataQuality = (availability) => {
    const availableMetrics = Object.values(availability).filter(v => v).length;
    const totalMetrics = Object.keys(availability).length;
    const percentage = Math.round((availableMetrics / totalMetrics) * 100);
    
    return {
      percentage,
      level: percentage >= 80 ? 'Excellent' :
             percentage >= 60 ? 'Good' :
             percentage >= 40 ? 'Fair' : 'Limited',
      availableMetrics,
      totalMetrics
    };
  };

  const getConnectedDevices = (deviceDataMap) => {
    const devices = [];
    
    Object.entries(deviceDataMap).forEach(([deviceType, records]) => {
      if (records.length > 0) {
        const latestRecord = records[0];
        devices.push({
          name: formatDeviceName(deviceType),
          type: deviceType,
          connected: true,
          lastSync: latestRecord.metadata?.receivedAt || latestRecord._metadata?.lastModified,
          battery: 100, // Would come from device data
          dataTypes: Object.keys(extractMetricsFromRecord(latestRecord, deviceType))
        });
      }
    });
    
    return devices;
  };

  const generateRecommendations = (data, scores) => {
    const recommendations = [];
    
    if (scores.components.activity < 70) {
      recommendations.push({
        category: 'activity',
        priority: 'high',
        message: 'Increase daily steps to reach 10,000 for optimal health',
        target: '10,000 steps/day'
      });
    }
    
    if (!data.sleep?.duration || data.sleep.duration < 7) {
      recommendations.push({
        category: 'sleep',
        priority: 'high',
        message: 'Aim for 7-9 hours of quality sleep',
        target: '7-9 hours'
      });
    }
    
    if (data.hydration?.glasses < 6) {
      recommendations.push({
        category: 'hydration',
        priority: 'medium',
        message: 'Increase water intake to stay properly hydrated',
        target: '8 glasses/day'
      });
    }
    
    return recommendations;
  };

  // Helper function to format device names
  const formatDeviceName = (deviceType) => {
    const nameMap = {
      'apple-watch': 'Apple Watch',
      'iphone-healthkit': 'iPhone Health',
      'garmin': 'Garmin',
      'fitbit': 'Fitbit',
      'samsung-galaxy-watch': 'Samsung Galaxy Watch',
      'withings': 'Withings',
      'withings-uscan': 'Withings U-Scan',
      'withings-sleep': 'Withings Sleep Analyzer',
      'withings-body': 'Withings Body+',
      'withings-bpm': 'Withings BPM',
      'oura': 'Oura Ring',
      'dexcom': 'Dexcom CGM',
      'dexcom-g6': 'Dexcom G6',
      'freestyle-libre': 'FreeStyle Libre',
      'omron': 'Omron',
      'omron-bp': 'Omron Blood Pressure',
      'polar': 'Polar',
      'eight-sleep': 'Eight Sleep',
      'tempdrop': 'Tempdrop',
      'android-health': 'Android Health'
    };
    return nameMap[deviceType] || deviceType;
  };

  // Fetch health data
  const fetchRealHealthData = useCallback(async () => {
    console.log('🚀 fetchRealHealthData started');
    
    if (isFetchingRef.current) {
      console.log('⏸️ Already fetching, skipping...');
      return;
    }

    const userId = user?.localAccountId || userInfo?.localAccountId;
    const country = userInfo?.country || 'United States';
    
    console.log('📌 Fetch health data - User ID:', userId);
    console.log('📌 Fetch health data - Country:', country);
    
    if (!userId) {
      console.log('❌ No user ID available');
      setIsLoading(false);
      return;
    }

    isFetchingRef.current = true;

    try {
      console.log('📡 Fetching health data from multiple sources...');
      
      // First, fetch profile data from Azure Data Lake
      const profileData = await fetchProfileDataFromLake();
      console.log('📊 Profile data fetched:', profileData);
      
      // Store profile data for use in transform
      const currentMedicationsData = profileData?.medicationsData || profileDataFromLake;
      const currentActivePlansData = profileData?.activePlansData || activePlansFromLake;
      
      console.log('💊 Current medications data:', currentMedicationsData);
      console.log('📋 Current active plans data:', currentActivePlansData);
      
      // Then fetch IoMT data - FIXED: Fetch 90 days to support all chart timeRanges (7D/30D/60D/90D)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90); // Changed from -7 to -90 days
      
      console.log('📡 Fetching IoMT data for 90 days to support all chart views...');
      console.log('📡 Date range:', startDate.toISOString().split('T')[0], 'to', endDate.toISOString().split('T')[0]);
      console.log('🎯 90-DAY FETCH: This should include September data!');
      const response = await iomtApiService.getIomtData(userId, {
        limit: 300, // Increased from 100 to 300 to handle more historical data
        onlyLatest: false,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });

      console.log('📥 IoMT API Response:', response);

      if (response?.success && response?.data?.length > 0) {
        console.log(`✅ Retrieved ${response.data.length} records from ${response.summary?.deviceSummary?.totalDevices || 0} devices`);
        
        response.data.slice(0, 3).forEach((record, idx) => {
          console.log(`Record ${idx + 1}:`, {
            fileName: record._metadata?.fileName,
            steps: record.healthMetrics?.activity?.steps?.value,
            deviceType: record._metadata?.deviceType
          });
        });
        
        // Wait a tick to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 0));
        
        const transformedData = transformAzureData(response, currentMedicationsData, currentActivePlansData);
        console.log('🎯 Transformed data:', transformedData);
        
        if (transformedData) {
          // Attach raw API response for historical data extraction
          transformedData._rawApiResponse = response;
          console.log('📎 ATTACHED: Raw API response for historical extraction');
          
          // DEBUG: Show all dates in the raw API response for troubleshooting
          if (response?.data && Array.isArray(response.data)) {
            const allDates = response.data.map(record => {
              const fileDate = record._metadata?.fileDate;
              const fileName = record._metadata?.fileName;
              return { fileDate, fileName };
            }).filter(item => item.fileDate);
            
            const uniqueDates = [...new Set(allDates.map(item => item.fileDate))].sort();
            console.log('📅 RAW API RESPONSE DATES:', uniqueDates);
            console.log('📅 TOTAL FILES IN RESPONSE:', response.data.length);
            console.log('📅 SEPTEMBER FILES:', allDates.filter(item => 
              item.fileDate && item.fileDate.includes('2025-09')
            ).length);
            console.log('📅 SAMPLE SEPTEMBER FILES:', allDates.filter(item => 
              item.fileDate && item.fileDate.includes('2025-09')
            ).slice(0, 5));
          }
          
          setHealthData(transformedData);
          setLastSync(new Date().toISOString());
          setError(null);
          
          setDeviceStatus({
            connected: true,
            lastSync: new Date().toISOString(),
            deviceCount: transformedData.devices.length,
            devices: transformedData.devices.map(d => ({
              type: d.type,
              name: d.name,
              lastSync: d.lastSync
            }))
          });
          
          console.log('📊 Data Quality:', transformedData.dataQuality);
          console.log('⚠️ Active Alerts:', transformedData.alerts.length);
          console.log('📱 Final steps value:', transformedData.activity?.steps?.value);
          console.log('💊 Medications loaded:', transformedData.medications?.length || 0);
          console.log('📋 Active plans loaded:', transformedData.activePlans?.length || 0);
        } else {
          console.log('⚠️ Transform failed, using empty data');
          setHealthData(EMPTY_HEALTH_DATA);
        }
      } else {
        console.log('⚠️ No health data available from IoMT');
        // Even with no IoMT data, we can still use profile data
        const profileOnlyData = transformAzureData({ data: [] }, currentMedicationsData, currentActivePlansData);
        console.log('📊 Using profile-only data:', profileOnlyData);
        // Still attach empty raw response for consistency
        profileOnlyData._rawApiResponse = { data: [] };
        console.log('📅 PROFILE-ONLY: No IoMT data available, using empty response');
        setHealthData(profileOnlyData);
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.message);
      // Even on error, try to at least load profile data
      const currentMedicationsData = profileDataFromLake;
      const currentActivePlansData = activePlansFromLake;
      const profileOnlyData = transformAzureData({ data: [] }, currentMedicationsData, currentActivePlansData);
      console.log('📊 Error fallback - using profile-only data:', profileOnlyData);
      // Still attach empty raw response for consistency
      profileOnlyData._rawApiResponse = { data: [] };
      console.log('📅 ERROR FALLBACK: No IoMT data due to error, using empty response');
      setHealthData(profileOnlyData);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
      console.log('✅ Fetch complete');
    }
  }, [user?.localAccountId, userInfo?.localAccountId, userInfo?.country, transformAzureData, EMPTY_HEALTH_DATA, fetchProfileDataFromLake, profileDataFromLake, activePlansFromLake]);

  // Manual refresh
  const refreshData = useCallback(() => {
    console.log('🔄 Manual refresh triggered');
    return fetchRealHealthData();
  }, [fetchRealHealthData]);

  // DEBUGGING: Expose refresh function to window for manual testing
  useEffect(() => {
    window.forceHealthDataRefresh = () => {
      console.log('🚀 FORCE REFRESH: Triggered from console');
      fetchRealHealthData();
    };
    return () => {
      delete window.forceHealthDataRefresh;
    };
  }, [fetchRealHealthData]);

  // Initial fetch
  useEffect(() => {
    const userId = user?.localAccountId || userInfo?.localAccountId;
    console.log('🎬 Initial fetch useEffect - userId:', userId);
    
    if (userId && !isFetchingRef.current) {
      console.log('🚀 Initial data fetch for user:', userId);
      fetchRealHealthData();
    }
  }, [user?.localAccountId, userInfo?.localAccountId]); // Remove fetchRealHealthData from deps to prevent loops

  // Auto-refresh setup - every 5 minutes for professional monitoring
  useEffect(() => {
    const userId = user?.localAccountId || userInfo?.localAccountId;
    if (!userId) return;

    console.log('⏰ Setting up auto-refresh (5 min interval)');
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      console.log('⏰ Auto-refresh triggered');
      fetchRealHealthData();
    }, 30000); // 30 seconds for immediate testing

    return () => {
      console.log('🛑 Cleaning up interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user?.localAccountId, userInfo?.localAccountId]); // Remove fetchRealHealthData from deps

  // Return stable object reference
  const returnValue = useMemo(() => ({
    healthData,
    isLoading,
    error,
    refreshData,
    deviceStatus,
    lastSync,
    dataAvailability,
    hasAnyData: healthData !== null && healthData.dataQuality?.availableMetrics > 0
  }), [healthData, isLoading, error, refreshData, deviceStatus, lastSync, dataAvailability]);
  
  // Reduce console logging to prevent spam
  // console.log('🔄 Hook returning:', returnValue);
  
  return returnValue;
};

export default useRealTimeHealthData;